import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { MdPrint, MdColorLens, MdInvertColors, MdCropPortrait, MdCropLandscape } from "react-icons/md";
import "./PrintProducts.css";
import { elakayAutoText } from "./dataElakay";

// The ONE Elakay print system (extends printerMyshop.js's existing "User Viewd Liste My Shop"
// print modal instead of duplicating it): owns the Print button, Black & White / Color toggle,
// the new Portrait / Landscape toggle, the react-to-print wiring, and the shared Elakay footer.
// Callers (printerMyshop.js's own PrintProductsPaper, and the new Provider My Products / Orders /
// Inventory print views) only supply their own printable table via `children`, so every print
// document keeps the identical Elakay chrome, color/B&W palette and page-break rules.
export default function PrintPaperShell({ infoSource, documentTitle, items, itemNoun, children }) {
  const componentRef = useRef();
  const [isColorMode, setIsColorMode] = useState(true);
  const [orientation, setOrientation] = useState("portrait");

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${documentTitle}-${isColorMode ? "Color" : "BW"}-${orientation}-${new Date().toISOString().split("T")[0]}`,
    pageStyle: `
      @page {
        size: letter ${orientation};
        margin: 10mm;
      }
    `,
  });

  const btnStyle = (bg, shadow) => ({
    display: "inline-flex", alignItems: "center", gap: "8px", padding: "12px 24px", background: bg,
    color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer",
    fontSize: "16px", boxShadow: shadow, transition: "all 0.3s ease",
  });
  const hover = (e, shadow) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = shadow; };
  const unhover = (e, shadow) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = shadow; };

  return (
    <div style={{ minWidth: "800px", padding: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={handlePrint}
          style={btnStyle("linear-gradient(135deg, #007399, #00a4d8)", "0 4px 12px rgba(0, 115, 153, 0.2)")}
          onMouseOver={(e) => hover(e, "0 6px 16px rgba(0, 115, 153, 0.3)")}
          onMouseOut={(e) => unhover(e, "0 4px 12px rgba(0, 115, 153, 0.2)")}
        >
          <MdPrint style={{ fontSize: "24px" }} />
          {infoSource?.laguangeElakay?.("Button", infoSource.languageAPI)?.printThisOut || elakayAutoText("h_pps_print", "Print this out!")}
        </button>

        <button
          onClick={() => setIsColorMode((v) => !v)}
          style={btnStyle(
            isColorMode ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, #6b7280, #4b5563)",
            isColorMode ? "0 4px 12px rgba(239, 68, 68, 0.2)" : "0 4px 12px rgba(107, 114, 128, 0.2)"
          )}
          onMouseOver={(e) => hover(e, isColorMode ? "0 6px 16px rgba(239, 68, 68, 0.3)" : "0 6px 16px rgba(107, 114, 128, 0.3)")}
          onMouseOut={(e) => unhover(e, isColorMode ? "0 4px 12px rgba(239, 68, 68, 0.2)" : "0 4px 12px rgba(107, 114, 128, 0.2)")}
        >
          {isColorMode ? <><MdInvertColors style={{ fontSize: "24px" }} /><span>{elakayAutoText("h_pps_bw", "Black & White")}</span></>
            : <><MdColorLens style={{ fontSize: "24px" }} /><span>{elakayAutoText("h_pps_color", "Color")}</span></>}
        </button>

        <div role="group" aria-label={elakayAutoText("h_pps_orientation", "Orientation")} style={{ display: "inline-flex", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          <button
            type="button" aria-pressed={orientation === "portrait"} onClick={() => setOrientation("portrait")}
            style={{ ...btnStyle(orientation === "portrait" ? "linear-gradient(135deg, #007399, #00a4d8)" : "#e5e7eb", "none"), color: orientation === "portrait" ? "white" : "#374151", borderRadius: 0 }}
          ><MdCropPortrait style={{ fontSize: "22px" }} /><span>{elakayAutoText("h_pps_portrait", "Portrait")}</span></button>
          <button
            type="button" aria-pressed={orientation === "landscape"} onClick={() => setOrientation("landscape")}
            style={{ ...btnStyle(orientation === "landscape" ? "linear-gradient(135deg, #007399, #00a4d8)" : "#e5e7eb", "none"), color: orientation === "landscape" ? "white" : "#374151", borderRadius: 0 }}
          ><MdCropLandscape style={{ fontSize: "22px" }} /><span>{elakayAutoText("h_pps_landscape", "Landscape")}</span></button>
        </div>

        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px", padding: "12px 20px",
          background: isColorMode ? "linear-gradient(135deg, rgba(0, 115, 153, 0.1), rgba(0, 164, 216, 0.08))" : "linear-gradient(135deg, rgba(107, 114, 128, 0.1), rgba(75, 85, 99, 0.08))",
          borderRadius: "8px", border: `1px solid ${isColorMode ? "rgba(0, 115, 153, 0.2)" : "rgba(107, 114, 128, 0.2)"}`,
          fontSize: "14px", fontWeight: "500", color: "#475569",
        }}>
          <span style={{ fontSize: "18px" }}>{isColorMode ? "🎨" : "⚫"}</span>
          <span>{items?.length || 0} {itemNoun} - {isColorMode ? elakayAutoText("h_983524e76b234c", "Color Mode") : elakayAutoText("h_0a0489027e23f9", "B&W Mode")}</span>
        </div>
      </div>

      <div style={{ background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
        <div ref={componentRef} className={`print-paper-wrapper ${isColorMode ? "color-mode" : "bw-mode"}`}>
          {typeof children === "function" ? children({ isColorMode, orientation }) : children}
          <div className="print-paper-footer">© {new Date().getFullYear()} {elakayAutoText("h_pps_footer", "Elakay - www.elakay.com - support@elakay.com")}</div>
        </div>
      </div>
    </div>
  );
}
